<template>
  <v-stepper v-model="step">
    <v-stepper-header>
      <v-stepper-step :complete="step > 1" step="1"
        >Laskun tyyppi</v-stepper-step
      >
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 2" step="2">Perustiedot</v-stepper-step>
      <v-divider></v-divider>

      <template v-if="claim.type === 'travel'">
        <v-stepper-step :complete="step > 3" step="3"
          >Päivärahat</v-stepper-step
        >
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 4" step="4"
          >Kilometrit</v-stepper-step
        >
        <v-divider></v-divider>
      </template>

      <v-stepper-step :complete="step > 5" step="5">Kulut</v-stepper-step>
      <v-divider></v-divider>
      <v-stepper-step step="6">Yhteenveto</v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <!-- Step 1: Type Selection -->
      <v-stepper-content step="1">
        <v-card class="mb-12 mt-4" flat>
          <div class="text-h6 mb-4">Valitse laskun tyyppi</div>
          <v-radio-group v-model="claim.type" column>
            <v-radio
              label="Matkalasku (Päivärahat, kilometrit, kulut) [ei käytössä]"
              value="travel"
              :disabled="true"
            ></v-radio>
            <v-radio label="Kululasku (Vain kulut)" value="expense"></v-radio>
          </v-radio-group>
        </v-card>
        <div class="d-flex flex-column flex-sm-row">
          <v-btn text @click="$emit('cancel')" class="mr-sm-auto mb-2 mb-sm-0">
            Peruuta
          </v-btn>
          <v-btn
            color="secondary"
            @click="saveDraft"
            :disabled="isReadOnly"
            :loading="loading"
            class="mr-sm-2 mb-2 mb-sm-0"
          >
            Tallenna luonnoksena
          </v-btn>
          <v-btn color="primary" @click="step = 2" :disabled="!claim.type"
            >Jatka</v-btn
          >
        </div>
      </v-stepper-content>

      <!-- Step 2: Basic Info -->
      <v-stepper-content step="2">
        <v-form ref="step2Form">
          <v-card class="mb-12 mt-4" flat>
            <v-text-field
              v-model="claim.memberName"
              label="Nimi"
              outlined
              :rules="requiredRules"
            ></v-text-field>
            <v-text-field
              v-model="claim.iban"
              label="IBAN"
              outlined
              :rules="ibanRules"
            ></v-text-field>
            <v-text-field
              v-model="claim.tripReason"
              label="Matkan / Kulun syy"
              outlined
              :rules="requiredRules"
            ></v-text-field>

            <div class="text-subtitle-1 mb-2">Kustannusten kohdistus</div>
            <v-row dense>
              <v-col
                cols="12"
                sm="6"
                md="4"
                v-for="(val, key) in claim.costAllocation"
                :key="key"
              >
                <v-checkbox
                  v-model="claim.costAllocation[key]"
                  :label="formatAllocationLabel(key)"
                  dense
                  hide-details
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-card>
          <div class="d-flex flex-column flex-sm-row">
            <v-btn text @click="step = 1" class="mr-sm-auto mb-2 mb-sm-0"
              >Takaisin</v-btn
            >
            <v-btn
              color="secondary"
              @click="saveDraft"
              :disabled="isReadOnly"
              :loading="loading"
              class="mr-sm-2 mb-2 mb-sm-0"
            >
              Tallenna luonnoksena
            </v-btn>
            <v-btn color="primary" @click="validateStep2">Jatka</v-btn>
          </div>
        </v-form>
      </v-stepper-content>

      <!-- Step 3: Daily Allowance -->
      <v-stepper-content step="3">
        <v-form ref="step3Form">
          <v-card class="mb-12 mt-4" flat>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="claim.dailyAllowance.startDate"
                  label="Alkamispäivä"
                  type="date"
                  outlined
                  class="mt-2"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="claim.dailyAllowance.startTime"
                  label="Alkamisaika"
                  type="time"
                  outlined
                  class="mt-2"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="claim.dailyAllowance.endDate"
                  label="Päättymispäivä"
                  type="date"
                  outlined
                  class="mt-2"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="claim.dailyAllowance.endTime"
                  label="Päättymisaika"
                  type="time"
                  outlined
                  class="mt-2"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model.number="claim.dailyAllowance.fullDayRate"
                  label="Kokopäiväraha (€)"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="claim.dailyAllowance.partialDayRate"
                  label="Osapäiväraha (€)"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row
              v-if="
                claim.dailyAllowance.fullDayCount > 0 ||
                claim.dailyAllowance.partialDayCount > 0
              "
            >
              <v-col cols="12">
                <div class="text-subtitle-2 mb-2">Ilmaiset ateriat</div>
                <v-alert dense type="info" text class="mb-2">
                  Jos olet saanut ilmaisen ruoan (2 ateriaa/kokopäivä tai 1
                  ateria/osapäivä), päiväraha puolitetaan kyseiseltä päivältä.
                </v-alert>
              </v-col>
              <v-col cols="6" v-if="claim.dailyAllowance.fullDayCount > 0">
                <v-text-field
                  v-model.number="claim.dailyAllowance.freeMealFullDays"
                  label="Päiviä joina 2 ilmaista ateriaa (kokopäivä)"
                  type="number"
                  min="0"
                  :max="claim.dailyAllowance.fullDayCount"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" v-if="claim.dailyAllowance.partialDayCount > 0">
                <v-text-field
                  v-model.number="claim.dailyAllowance.freeMealPartialDays"
                  label="Päiviä joina 1 ilmainen ateria (osapäivä)"
                  type="number"
                  min="0"
                  :max="claim.dailyAllowance.partialDayCount"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-alert type="info" text dense>
              Kokopäivärahat: {{ claim.dailyAllowance.fullDayCount }} kpl ({{
                formatCurrency(claim.dailyAllowance.fullDayTotal)
              }})<br />
              Osapäivärahat: {{ claim.dailyAllowance.partialDayCount }} kpl ({{
                formatCurrency(claim.dailyAllowance.partialDayTotal)
              }})
            </v-alert>
          </v-card>
          <div class="d-flex flex-column flex-sm-row">
            <v-btn text @click="step = 2" class="mr-sm-auto mb-2 mb-sm-0"
              >Takaisin</v-btn
            >
            <v-btn
              color="secondary"
              @click="saveDraft"
              :disabled="isReadOnly"
              :loading="loading"
              class="mr-sm-2 mb-2 mb-sm-0"
            >
              Tallenna luonnoksena
            </v-btn>
            <v-btn color="primary" @click="validateStep3">Jatka</v-btn>
          </div>
        </v-form>
      </v-stepper-content>

      <!-- Step 4: Mileage -->
      <v-stepper-content step="4">
        <v-form ref="step4Form">
          <v-card class="mb-12 mt-4" flat>
            <v-btn small color="secondary" class="mb-4" @click="addMileageRow"
              >Lisää rivi</v-btn
            >
            <div
              v-for="(trip, index) in claim.mileage"
              :key="index"
              class="mb-4 pa-4 grey lighten-5 rounded"
            >
              <v-row dense>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="trip.date"
                    label="Pvm"
                    type="date"
                    dense
                    outlined
                    hide-details="auto"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="9">
                  <v-text-field
                    v-model="trip.route"
                    label="Reitti"
                    dense
                    outlined
                    hide-details="auto"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="trip.passengers"
                    label="Matkustajat (lkm)"
                    type="number"
                    min="0"
                    dense
                    outlined
                    hide-details
                    @input="onPassengerCountChange(trip)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="trip.passengerNames"
                    label="Matkustajien nimet"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model.number="trip.distanceKm"
                    label="Km"
                    type="number"
                    dense
                    outlined
                    hide-details="auto"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <div class="text-caption mb-1">Hinta</div>
                  <div
                    v-for="rate in mileageRates"
                    :key="rate.value"
                    class="d-flex align-center mb-1"
                  >
                    <v-checkbox
                      v-model="trip.selectedRateValues"
                      :value="rate.value"
                      :label="rate.label"
                      dense
                      hide-details
                      class="ma-0 pa-0"
                      @change="updateMileageUnitPrice(trip)"
                    ></v-checkbox>
                  </div>
                  <div class="text-caption mt-1 font-weight-bold">
                    Yht: {{ formatCurrency(trip.unitPrice) }} / km
                  </div>
                  <div class="text-caption mt-1 font-weight-bold">
                    Korvaus:
                    {{ formatCurrency(trip.distanceKm * trip.unitPrice) }}
                  </div>
                </v-col>
              </v-row>
              <div class="d-flex justify-end mt-2">
                <v-btn icon color="error" small @click="removeMileageRow(index)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </v-card>
          <div class="d-flex flex-column flex-sm-row">
            <v-btn text @click="step = 3" class="mr-sm-auto mb-2 mb-sm-0"
              >Takaisin</v-btn
            >
            <v-btn
              color="secondary"
              @click="saveDraft"
              :disabled="isReadOnly"
              :loading="loading"
              class="mr-sm-2 mb-2 mb-sm-0"
            >
              Tallenna luonnoksena
            </v-btn>
            <v-btn color="primary" @click="validateStep4">Jatka</v-btn>
          </div>
        </v-form>
      </v-stepper-content>

      <!-- Step 5: Receipts -->
      <v-stepper-content step="5">
        <v-form ref="step5Form">
          <v-card class="mb-12 mt-4" flat>
            <v-btn small color="secondary" class="mb-4" @click="addReceiptRow"
              >Lisää kuitti</v-btn
            >
            <div
              v-for="(receipt, index) in claim.receipts"
              :key="index"
              class="mb-4 pa-4 grey lighten-5 rounded"
            >
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="receipt.date"
                    label="Pvm"
                    type="date"
                    dense
                    outlined
                    hide-details="auto"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="8" class="d-flex align-center justify-end">
                  <span class="text-subtitle-2 mr-2">Yhteensä:</span>
                  <span class="text-h6">{{
                    formatCurrency(getReceiptTotal(receipt))
                  }}</span>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>

              <div class="mb-2 text-subtitle-2">Rivit</div>
              <div
                v-for="(line, lineIndex) in receipt.lines"
                :key="'line_' + lineIndex"
                class="mb-2"
                :class="{
                  'grey lighten-4 pa-3 rounded elevation-1 mb-4':
                    $vuetify.breakpoint.smAndDown,
                }"
              >
                <v-row dense align="center">
                  <v-col cols="12" md="5">
                    <v-textarea
                      v-model="line.description"
                      label="Kuvaus"
                      dense
                      outlined
                      hide-details="auto"
                      :rules="requiredRules"
                      rows="1"
                      auto-grow
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="line.quantity"
                      label="Määrä"
                      type="number"
                      dense
                      outlined
                      hide-details="auto"
                      @input="updateLineTotal(line)"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="line.unitPrice"
                      label="á (€)"
                      type="number"
                      dense
                      outlined
                      hide-details="auto"
                      @input="updateLineTotal(line)"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="line.amount"
                      label="Yht (€)"
                      type="number"
                      dense
                      outlined
                      hide-details="auto"
                      :rules="requiredRules"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="1"
                    class="d-flex justify-end justify-md-center"
                  >
                    <v-btn
                      icon
                      color="error"
                      @click="removeReceiptLine(index, lineIndex)"
                      :disabled="receipt.lines.length === 1"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
              <v-btn
                small
                text
                color="primary"
                @click="addReceiptLine(index)"
                class="mb-3"
              >
                <v-icon left small>mdi-plus</v-icon> Lisää rivi
              </v-btn>

              <v-row dense align="center">
                <v-col cols="12" sm>
                  <div
                    v-if="receipt.imageUrl"
                    class="mb-2"
                    style="position: relative; display: inline-block"
                  >
                    <v-img
                      :src="receipt.imageUrl"
                      max-height="200"
                      max-width="200"
                      contain
                      class="grey lighten-2 rounded"
                      @click="openImage(receipt.imageUrl)"
                      style="cursor: pointer"
                    ></v-img>
                    <v-btn
                      icon
                      small
                      color="error"
                      style="
                        position: absolute;
                        top: 0;
                        right: 0;
                        background: rgba(255, 255, 255, 0.7);
                      "
                      @click="removeReceiptImage(index)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <file-upload
                    v-else
                    :ref="'fileUpload_' + index"
                    :multiple="false"
                    :compress="false"
                    label="Lisää kuitti (kuva)"
                    accepted-file-types="image/*"
                    @updatefiles="updateReceiptImage($event, index)"
                  ></file-upload>
                </v-col>
                <v-col cols="12" sm="auto">
                  <v-btn
                    color="deep-purple accent-3"
                    dark
                    @click="analyzeReceipt(index)"
                    :loading="analyzingReceipt === index"
                    block
                    class="mt-2 mt-sm-0"
                  >
                    <v-icon left>mdi-creation</v-icon>
                    Hae rivit ja summat tekoälyllä
                  </v-btn>
                </v-col>
              </v-row>
              <div class="d-flex justify-end mt-2">
                <v-btn icon color="error" small @click="removeReceiptRow(index)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </v-card>
          <div class="d-flex flex-column flex-sm-row">
            <v-btn
              text
              @click="prevStepFromReceipts"
              class="mr-sm-auto mb-2 mb-sm-0"
              >Takaisin</v-btn
            >
            <v-btn
              color="secondary"
              @click="saveDraft"
              :disabled="isReadOnly"
              :loading="loading"
              class="mr-sm-2 mb-2 mb-sm-0"
            >
              Tallenna luonnoksena
            </v-btn>
            <v-btn color="primary" @click="validateStep5">Jatka</v-btn>
          </div>
        </v-form>
      </v-stepper-content>

      <!-- Step 6: Summary -->
      <v-stepper-content step="6">
        <v-card class="mb-12 mt-4" flat>
          <v-simple-table>
            <tbody>
              <template v-if="claim.type === 'travel'">
                <tr>
                  <td>Päivärahat</td>
                  <td class="text-right">
                    {{ formatCurrency(totals.dailyAllowance) }}
                  </td>
                </tr>
                <tr>
                  <td>Kilometrit</td>
                  <td class="text-right">
                    {{ formatCurrency(totals.mileage) }}
                  </td>
                </tr>
              </template>
              <tr>
                <td>Kuitit</td>
                <td class="text-right">
                  {{ formatCurrency(totals.receipts) }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>YHTEENSÄ</td>
                <td class="text-right">
                  {{ formatCurrency(totals.grandTotal) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="mt-4 mb-4">
            <div class="text-subtitle-1 mb-2">Allekirjoitus</div>
            <div
              v-if="claim.signatureUrl && !isEditingSignature"
              class="mb-2"
              style="position: relative; display: inline-block"
            >
              <v-img
                :src="claim.signatureUrl"
                max-height="150"
                max-width="300"
                contain
                class="grey lighten-4 rounded border"
              ></v-img>
              <v-btn
                small
                text
                color="primary"
                @click="isEditingSignature = true"
                v-if="!isReadOnly"
              >
                Muokkaa allekirjoitusta
              </v-btn>
            </div>

            <div
              v-if="(!claim.signatureUrl || isEditingSignature) && !isReadOnly"
            >
              <div style="border: 1px solid #ccc; border-radius: 4px">
                <VueSignaturePad
                  width="100%"
                  height="200px"
                  ref="signaturePad"
                  :options="{ penColor: 'black' }"
                  @begin="onSignatureBegin"
                />
              </div>
              <div class="d-flex mt-2">
                <v-btn small text @click="clearSignature">Tyhjennä</v-btn>
                <v-btn small text @click="undoSignature">Kumoa</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  small
                  color="primary"
                  @click="saveSignature"
                  :disabled="isSignatureEmpty"
                >
                  OK
                </v-btn>
              </div>
            </div>
          </div>

          <v-checkbox
            v-model="claim.certified"
            label="Allekirjoituksellani todistan matkalaskun tiedot oikeiksi"
            :disabled="isReadOnly"
          ></v-checkbox>
          <v-alert v-if="isReadOnly" type="warning" dense class="mt-2">
            Et voi muokata tätä kululaskua koska se on tilassa:
            {{ claim.status }}
          </v-alert>
        </v-card>
        <div class="d-flex flex-column flex-sm-row">
          <v-btn text @click="step = 5" class="mr-sm-auto mb-2 mb-sm-0"
            >Takaisin</v-btn
          >
          <v-btn
            color="info"
            @click="printPDF"
            class="mr-sm-2 mb-2 mb-sm-0"
            :disabled="isReadOnly"
          >
            Tulosta PDF
          </v-btn>
          <v-btn
            color="secondary"
            @click="saveDraft"
            :disabled="isReadOnly"
            :loading="loading"
            class="mr-sm-2 mb-2 mb-sm-0"
          >
            Tallenna luonnoksena
          </v-btn>
          <v-btn
            color="success"
            @click="submit('Pending')"
            :disabled="!claim.certified || isReadOnly"
            :loading="loading"
          >
            Lähetä
          </v-btn>
        </div>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import dayjs from "dayjs";
import { firestore as db, storage } from "@/plugins/firebase.app";
import api from "@/api";
import { collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import FileUpload from "@/components/FileUpload";
import { generateClaimPDF } from "@/utils/pdfGenerator";

export default {
  name: "ExpenseClaimForm",
  components: { FileUpload },
  props: {
    initialClaim: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      step: 1,
      isEditingSignature: false,
      isSignatureEmpty: true,
      claim: {
        type: "expense",
        signatureUrl: null,
        memberName: "",
        iban: "",
        tripReason: "",
        costAllocation: {
          halytysosasto: false,
          nuorisoOsasto: false,
          varhaisnuorisoOsasto: false,
          naisosasto: false,
          veteraaniOsasto: false,
          yhdistystoiminta: false,
          leirikoski: false,
        },
        dailyAllowance: {
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
          fullDayCount: 0,
          fullDayTotal: 0,
          partialDayCount: 0,
          partialDayTotal: 0,
          fullDayRate: 54,
          partialDayRate: 25,
          freeMealFullDays: 0,
          freeMealPartialDays: 0,
        },
        mileage: [],
        receipts: [],
        certified: false,
      },
      analyzingReceipt: null,
      mileageRates: [
        { label: "Oma auto (0,55€)", value: "car", price: 0.55 },
        { label: "Perävaunu (0,09€)", value: "trailer", price: 0.09 },
        {
          label: "Matkatavara (yli 80kg) (0,04€)",
          value: "heavy",
          price: 0.04,
        },
        { label: "Matkustaja (0,04€)", value: "passenger", price: 0.04 },
      ],
      ibanRules: [
        (v) => !!v || "IBAN vaaditaan",
        (v) =>
          /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(
            (v || "").replace(/\s/g, "")
          ) || "Virheellinen IBAN",
      ],
      requiredRules: [(v) => !!v || "Pakollinen tieto"],
      loading: false,
    };
  },
  created() {
    if (this.initialClaim) {
      this.claim = JSON.parse(JSON.stringify(this.initialClaim));

      // Ensure type exists
      if (!this.claim.type) {
        this.$set(this.claim, "type", "travel");
      }

      // Migrate old receipts to new structure (lines array)
      if (this.claim.receipts) {
        this.claim.receipts = this.claim.receipts.map((r) => {
          if (!r.lines) {
            return {
              ...r,
              lines: [
                {
                  description: r.description || "",
                  amount: r.amount || 0,
                },
              ],
            };
          }
          return r;
        });
      }

      // Ensure daily allowance rates exist for old claims
      if (!this.claim.dailyAllowance.fullDayRate) {
        this.$set(this.claim.dailyAllowance, "fullDayRate", 54);
      }
      if (!this.claim.dailyAllowance.partialDayRate) {
        this.$set(this.claim.dailyAllowance, "partialDayRate", 25);
      }
      if (this.claim.dailyAllowance.freeMealFullDays === undefined) {
        this.$set(this.claim.dailyAllowance, "freeMealFullDays", 0);
      }
      if (this.claim.dailyAllowance.freeMealPartialDays === undefined) {
        this.$set(this.claim.dailyAllowance, "freeMealPartialDays", 0);
      }

      // Ensure mileage rates from claim are available in dropdown if they differ
      this.claim.mileage.forEach((trip) => {
        // Initialize selectedRateValues if missing (migration)
        if (!trip.selectedRateValues) {
          // Legacy migration: map unitPrice to IDs
          const newIds = [];
          // Try to match exact price first
          const exactMatch = this.mileageRates.find(
            (r) => Math.abs(r.price - trip.unitPrice) < 0.001
          );
          if (exactMatch) {
            newIds.push(exactMatch.value);
          } else {
            // Check if it's a combination (e.g. car + trailer = 0.64)
            // This is a simple heuristic for common cases
            let remaining = trip.unitPrice;
            const car = this.mileageRates.find((r) => r.value === "car");
            const trailer = this.mileageRates.find(
              (r) => r.value === "trailer"
            );

            if (
              car &&
              trailer &&
              Math.abs(remaining - (car.price + trailer.price)) < 0.001
            ) {
              newIds.push("car", "trailer");
            } else {
              // Custom rate
              const customId = "custom_" + trip.unitPrice;
              this.mileageRates.push({
                label: `Muu (${this.formatCurrency(trip.unitPrice)})`,
                value: customId,
                price: trip.unitPrice,
              });
              newIds.push(customId);
            }
          }
          this.$set(trip, "selectedRateValues", newIds);
        } else {
          // Migration from [price, price] to [id, id] (if user used the buggy version)
          // Check if values are numbers
          if (
            trip.selectedRateValues.length > 0 &&
            typeof trip.selectedRateValues[0] === "number"
          ) {
            const newIds = [];
            trip.selectedRateValues.forEach((val) => {
              const rate = this.mileageRates.find(
                (r) => Math.abs(r.price - val) < 0.001
              );
              if (rate) {
                newIds.push(rate.value);
              } else {
                const customId = "custom_" + val;
                if (!this.mileageRates.find((r) => r.value === customId)) {
                  this.mileageRates.push({
                    label: `Muu (${this.formatCurrency(val)})`,
                    value: customId,
                    price: val,
                  });
                }
                newIds.push(customId);
              }
            });
            this.$set(trip, "selectedRateValues", newIds);
          }
        }
      });
    } else if (this.$user) {
      this.claim.memberName = this.$user.displayName || "";
    }
  },
  computed: {
    isReadOnly() {
      if (!this.claim.id) return false;
      if (this.$isTreasurer) return false;
      return this.claim.status && this.claim.status !== "Pending";
    },
    totals() {
      const dailyAllowance =
        this.claim.dailyAllowance.fullDayTotal +
        this.claim.dailyAllowance.partialDayTotal;
      const mileage = this.claim.mileage.reduce(
        (sum, item) => sum + (item.distanceKm * item.unitPrice || 0),
        0
      );
      const receipts = this.claim.receipts.reduce((sum, receipt) => {
        const receiptTotal = receipt.lines
          ? receipt.lines.reduce((lSum, line) => lSum + (line.amount || 0), 0)
          : receipt.amount || 0;
        return sum + receiptTotal;
      }, 0);
      return {
        dailyAllowance,
        mileage,
        receipts,
        grandTotal: dailyAllowance + mileage + receipts,
      };
    },
  },
  watch: {
    step(val) {
      if (val === 6) {
        this.$nextTick(() => {
          if (this.$refs.signaturePad) {
            this.$refs.signaturePad.resizeCanvas();
          }
        });
      }
    },
    "claim.dailyAllowance": {
      handler: "calculateDailyAllowance",
      deep: true,
    },
    "claim.dailyAllowance.fullDayRate": "calculateDailyAllowance",
    "claim.dailyAllowance.partialDayRate": "calculateDailyAllowance",
    "claim.dailyAllowance.freeMealFullDays": "calculateDailyAllowance",
    "claim.dailyAllowance.freeMealPartialDays": "calculateDailyAllowance",
  },
  methods: {
    validateStep2() {
      if (this.$refs.step2Form.validate()) {
        this.nextStepAfterBasicInfo();
      }
    },
    validateStep3() {
      if (this.$refs.step3Form.validate()) {
        this.step = 4;
      }
    },
    validateStep4() {
      if (this.$refs.step4Form.validate()) {
        this.step = 5;
      }
    },
    validateStep5() {
      if (this.$refs.step5Form.validate()) {
        this.step = 6;
      }
    },
    nextStepAfterBasicInfo() {
      if (this.claim.type === "expense") {
        this.step = 5;
      } else {
        this.step = 3;
      }
    },
    prevStepFromReceipts() {
      if (this.claim.type === "expense") {
        this.step = 2;
      } else {
        this.step = 4;
      }
    },
    formatAllocationLabel(key) {
      const labels = {
        halytysosasto: "Hälytysosasto",
        nuorisoOsasto: "Nuoriso-osasto",
        varhaisnuorisoOsasto: "Varhaisnuoriso-osasto",
        naisosasto: "Tukiosasto",
        veteraaniOsasto: "Veteraaniosasto",
        yhdistystoiminta: "Yhdistystoiminta",
        leirikoski: "Leirikoski",
      };
      return labels[key] || key;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fi-FI", {
        style: "currency",
        currency: "EUR",
      }).format(value || 0);
    },
    addMileageRow() {
      this.claim.mileage.push({
        date: "",
        route: "",
        passengers: 0,
        distanceKm: 0,
        unitPrice: 0.55,
        selectedRateValues: ["car"],
      });
    },
    updateMileageUnitPrice(trip) {
      if (trip.selectedRateValues && trip.selectedRateValues.length > 0) {
        trip.unitPrice = trip.selectedRateValues.reduce((sum, id) => {
          const rate = this.mileageRates.find((r) => r.value === id);
          if (!rate) return sum;

          if (id === "passenger") {
            const count = parseInt(trip.passengers) || 0;
            return sum + rate.price * count;
          }

          return sum + rate.price;
        }, 0);
      } else {
        trip.unitPrice = 0;
      }
    },
    onPassengerCountChange(trip) {
      const count = parseInt(trip.passengers) || 0;
      const hasPassengerRate = trip.selectedRateValues.includes("passenger");

      if (count > 0 && !hasPassengerRate) {
        trip.selectedRateValues.push("passenger");
      } else if (count === 0 && hasPassengerRate) {
        const idx = trip.selectedRateValues.indexOf("passenger");
        if (idx > -1) {
          trip.selectedRateValues.splice(idx, 1);
        }
      }
      this.updateMileageUnitPrice(trip);
    },
    removeMileageRow(index) {
      this.claim.mileage.splice(index, 1);
    },
    addReceiptRow() {
      this.claim.receipts.push({
        date: "",
        lines: [{ description: "", quantity: 1, unitPrice: 0, amount: 0 }],
      });
    },
    removeReceiptRow(index) {
      this.claim.receipts.splice(index, 1);
    },
    addReceiptLine(receiptIndex) {
      this.claim.receipts[receiptIndex].lines.push({
        description: "",
        quantity: 1,
        unitPrice: 0,
        amount: 0,
      });
    },
    updateLineTotal(line) {
      if (line.quantity && line.unitPrice) {
        line.amount = parseFloat((line.quantity * line.unitPrice).toFixed(2));
      }
    },
    removeReceiptLine(receiptIndex, lineIndex) {
      this.claim.receipts[receiptIndex].lines.splice(lineIndex, 1);
    },
    getReceiptTotal(receipt) {
      if (!receipt.lines) return receipt.amount || 0;
      return receipt.lines.reduce((sum, line) => sum + (line.amount || 0), 0);
    },
    calculateDailyAllowance() {
      const { startDate, startTime, endDate, endTime } =
        this.claim.dailyAllowance;
      if (startDate && startTime && endDate && endTime) {
        const start = dayjs(`${startDate}T${startTime}`);
        const end = dayjs(`${endDate}T${endTime}`);
        const totalHours = end.diff(start, "hour", true);

        if (totalHours <= 0) {
          this.claim.dailyAllowance.fullDayCount = 0;
          this.claim.dailyAllowance.partialDayCount = 0;
          this.claim.dailyAllowance.fullDayTotal = 0;
          this.claim.dailyAllowance.partialDayTotal = 0;
          return;
        }

        let fullDays = Math.floor(totalHours / 24);
        const remainingHours = totalHours % 24;
        let partialDays = 0;

        if (remainingHours > 10) {
          fullDays += 1;
        } else if (remainingHours > 6) {
          partialDays = 1;
        }

        this.claim.dailyAllowance.fullDayCount = fullDays;
        this.claim.dailyAllowance.partialDayCount = partialDays;

        // Clamp free meal days to actual days
        if (
          this.claim.dailyAllowance.freeMealFullDays >
          this.claim.dailyAllowance.fullDayCount
        ) {
          this.claim.dailyAllowance.freeMealFullDays =
            this.claim.dailyAllowance.fullDayCount;
        }
        if (
          this.claim.dailyAllowance.freeMealPartialDays >
          this.claim.dailyAllowance.partialDayCount
        ) {
          this.claim.dailyAllowance.freeMealPartialDays =
            this.claim.dailyAllowance.partialDayCount;
        }

        const fullRate = this.claim.dailyAllowance.fullDayRate;
        const partialRate = this.claim.dailyAllowance.partialDayRate;

        const fullDaysNormal =
          this.claim.dailyAllowance.fullDayCount -
          (this.claim.dailyAllowance.freeMealFullDays || 0);
        const fullDaysReduced = this.claim.dailyAllowance.freeMealFullDays || 0;

        const partialDaysNormal =
          this.claim.dailyAllowance.partialDayCount -
          (this.claim.dailyAllowance.freeMealPartialDays || 0);
        const partialDaysReduced =
          this.claim.dailyAllowance.freeMealPartialDays || 0;

        this.claim.dailyAllowance.fullDayTotal =
          fullDaysNormal * fullRate + fullDaysReduced * fullRate * 0.5;
        this.claim.dailyAllowance.partialDayTotal =
          partialDaysNormal * partialRate +
          partialDaysReduced * partialRate * 0.5;
      }
    },
    async analyzeReceipt(index) {
      const receipt = this.claim.receipts[index];
      let base64Data = null;
      let mimeType = null;
      let imageUrl = null;

      if (receipt.imageUrl) {
        imageUrl = receipt.imageUrl;
      } else {
        const refName = "fileUpload_" + index;
        const uploadComponent = this.$refs[refName] && this.$refs[refName][0];

        if (!uploadComponent) return;

        const files = await uploadComponent.getFiles();
        if (!files || files.length === 0) {
          this.$toast.error("Lisää ensin kuva");
          return;
        }

        const fileObj = files[0];
        const imageBase64 = fileObj.content; // "data:image/jpeg;base64,..."

        if (!imageBase64 || !imageBase64.includes(",")) {
          this.$toast.error("Virheellinen kuvatiedosto");
          return;
        }

        base64Data = imageBase64.split(",")[1];
        mimeType = imageBase64.split(";")[0].split(":")[1];
      }

      this.analyzingReceipt = index;
      try {
        const result = await api.analyzeReceipt({
          imageBase64: base64Data,
          mimeType,
          imageUrl,
        });

        if (result.data.success) {
          const data = result.data.data;
          const receipt = this.claim.receipts[index];

          if (data.date) receipt.date = data.date;
          if (data.lines && data.lines.length > 0) {
            receipt.lines = data.lines.map((l) => ({
              description: l.description,
              quantity: l.quantity || 1,
              unitPrice: l.unitPrice || 0,
              amount: l.amount,
            }));
          }

          this.$toast.success("Kuitti analysoitu onnistuneesti");
        } else {
          this.$toast.error("Analysointi epäonnistui: " + result.data.error);
        }
      } catch (error) {
        console.error(error);
        this.$toast.error("Virhe analysoinnissa");
      } finally {
        this.analyzingReceipt = null;
      }
    },
    updateReceiptImage(files, index) {
      // This method is called when files change in FileUpload
      // We don't strictly need to do anything here if we fetch files on submit
      // but we could validate or show preview if needed.
    },
    removeReceiptImage(index) {
      this.claim.receipts[index].imageUrl = null;
    },
    openImage(url) {
      window.open(url, "_blank");
    },
    onSignatureBegin() {
      this.isSignatureEmpty = false;
    },
    clearSignature() {
      this.$refs.signaturePad.clearSignature();
      this.isSignatureEmpty = true;
    },
    undoSignature() {
      this.$refs.signaturePad.undoSignature();
    },
    saveSignature() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      console.log(
        "Saving signature. Empty:",
        isEmpty,
        "Data length:",
        data ? data.length : 0
      );
      if (isEmpty) {
        this.$toast.error("Allekirjoitus on tyhjä");
        return;
      }
      this.$set(this.claim, "signatureUrl", data);
      this.isEditingSignature = false;
    },
    async saveDraft() {
      await this.submit("Draft");
    },
    async printPDF() {
      try {
        const claimWithTotals = { ...this.claim, totals: this.totals };
        const doc = await generateClaimPDF(claimWithTotals);
        const safeName = (this.claim.memberName || "user")
          .replace(/[^a-z0-9]/gi, "_")
          .toLowerCase();
        doc.save(`matkalasku_${safeName}_${dayjs().format("YYYY-MM-DD")}.pdf`);
      } catch (e) {
        console.error("PDF generation failed", e);
        this.$toast.error("PDF:n luonti epäonnistui");
      }
    },
    async submit(status = "Pending") {
      this.loading = true;
      try {
        console.log("Submitting claim...", status);

        // Auto-save signature if pad is visible and has content
        if (
          (!this.claim.signatureUrl || this.isEditingSignature) &&
          !this.isReadOnly &&
          this.$refs.signaturePad
        ) {
          const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
          if (!isEmpty) {
            this.$set(this.claim, "signatureUrl", data);
            console.log("Auto-saved signature before submit");
          }
        }

        // Handle Signature Upload
        let signatureUrl = this.claim.signatureUrl;
        console.log(
          "Initial signatureUrl:",
          signatureUrl ? signatureUrl.substring(0, 50) + "..." : "null"
        );

        if (signatureUrl && signatureUrl.startsWith("data:image")) {
          console.log("Uploading signature...");
          const storageRef = ref(
            storage,
            `expense_claims/${this.$user.uid}/${Date.now()}_signature.png`
          );
          await uploadString(storageRef, signatureUrl, "data_url");
          signatureUrl = await getDownloadURL(storageRef);
          console.log("Signature uploaded:", signatureUrl);

          // Update local state to reflect the uploaded URL
          this.$set(this.claim, "signatureUrl", signatureUrl);
        } else {
          console.log("Signature is already a URL or null, skipping upload.");
        }

        // Process receipts to include images
        const processedReceipts = await Promise.all(
          this.claim.receipts.map(async (receipt, index) => {
            const refName = "fileUpload_" + index;
            const uploadComponent =
              this.$refs[refName] && this.$refs[refName][0];
            let imageUrl = receipt.imageUrl; // Keep existing if any

            if (uploadComponent) {
              const files = await uploadComponent.getFiles();
              if (files && files.length > 0 && files[0].content) {
                // Upload to Firebase Storage
                const file = files[0];
                const storageRef = ref(
                  storage,
                  `expense_claims/${this.$user.uid}/${Date.now()}_${index}_${
                    file.filename || "receipt.jpg"
                  }`
                );
                await uploadString(storageRef, file.content, "data_url");
                imageUrl = await getDownloadURL(storageRef);
              }
            }
            return { ...receipt, imageUrl: imageUrl || null };
          })
        );

        const payload = JSON.parse(
          JSON.stringify({
            ...this.claim,
            signatureUrl: signatureUrl || null,
            receipts: processedReceipts,
            totals: this.totals,
            submissionDate: dayjs().format("YYYY-MM-DD"),
            status: status,
            memberId: this.$user.uid,
            createdAt: this.claim.createdAt || new Date().toISOString(),
          })
        );

        console.log("Final payload:", payload);

        const claimsRef = collection(db, "expense_claims");

        if (this.claim.id) {
          // Update existing
          const docRef = doc(db, "expense_claims", this.claim.id);
          await updateDoc(docRef, payload);
        } else {
          // Create new
          await addDoc(claimsRef, payload);
        }

        if (status === "Draft") {
          this.$toast.success("Luonnos tallennettu");
        } else {
          this.$toast.success("Kululasku tallennettu");
        }
        this.$emit("saved");
      } catch (e) {
        console.error("Failed to save expense claim", e);
        this.$toast.error("Virhe tallennettaessa kululaskua");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
